/**
 * Single source of truth for plan rules shown in the UI. Pricing, Terms,
 * Profile and Home read from here so the wording cannot drift apart.
 * The trial numbers come from shared/tiers.ts (the gate reads the same constants).
 */
import { PROFESSIONAL_TRIAL_LIMIT, PREMIUM_TRIAL_LIMIT } from "@shared/tiers";
import { SITE_FACTS } from "@shared/siteFacts";
import { CHECKOUT_PLAN_VALUES } from "@shared/purchaseEvent";

export const FREE_DAILY_USES = PROFESSIONAL_TRIAL_LIMIT;
export const PREMIUM_DAILY_USES = PREMIUM_TRIAL_LIMIT;
export const DAY_PASS_PRICE = "$1";
export const MONTHLY_PRICE = "$4.99";
const usd = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
export const PREMIUM_DAY_PASS_PRICE = usd(CHECKOUT_PLAN_VALUES.premium.day_pass);
export const PREMIUM_MONTHLY_PRICE = usd(CHECKOUT_PLAN_VALUES.premium.monthly);

/**
 * Wording for the trial-limit / download paywall (owner, 31 Oct 2026): the Day Pass is the main button and the monthly pass is
 * presented as the saver. Prices come from the constants above; a test pins that the monthly price really is below five day passes.
 */
export function paywallOffer(premium: boolean) {
  const dayPass = premium ? PREMIUM_DAY_PASS_PRICE : DAY_PASS_PRICE;
  const monthly = premium ? PREMIUM_MONTHLY_PRICE : MONTHLY_PRICE;
  return {
    dayPass,
    monthly,
    dayPassLabel: `Buy Day Pass \u2014 ${dayPass}`,
    monthlyLabel: `Monthly pass \u2014 ${monthly}/month`,
    saverLine: `Save big on day pass with our monthly pass at just ${monthly}`,
  };
}
/**
 * Owner rule (31 Oct 2026): the Day Pass and the monthly plan never cover a Workflow Product. Each one has its own pricing.
 * One sentence, read from here by the paywall dialog, Pricing and the FAQ so it cannot drift.
 */
export const WORKFLOW_PRODUCT_NAMES = ["Book Writing Toolbox", "Event Suite", "Seller Studio", "Legal Production Desk"] as const;
/** "Book Writing Toolbox, Event Suite, Seller Studio and Legal Production Desk": the four Workflow Products, in one place, so no sentence can leave one out. */
export const WORKFLOW_PRODUCT_LIST = `${WORKFLOW_PRODUCT_NAMES.slice(0, -1).join(", ")} and ${WORKFLOW_PRODUCT_NAMES[WORKFLOW_PRODUCT_NAMES.length - 1]}`;
export const WORKFLOW_NOT_INCLUDED_NOTE =
  `Workflow Products (${WORKFLOW_PRODUCT_LIST}) have their own pricing and are not included in the Day Pass or the monthly plan.`;
/** Organization Premium Applications seats: none of the Workflow Products comes with them (the Professional Tools blocks include Event Suite). */
export const ORG_PREMIUM_WORKFLOW_NOTE = `Workflow Products (${WORKFLOW_PRODUCT_LIST}) are not included.`;
/** The /pricing overview. The page intro and the meta description are both built here, so they cannot drift from the four products. */
export function pricingOverview(tail: string, trialDays: number): string {
  return `Professional Tools from $1 a day or $4.99 a month. Premium Applications from $3 a day or $9.99 a month. Workflow Products (${WORKFLOW_PRODUCT_LIST}) from $29.99 a month, each with a ${trialDays}-day trial. ${tail}`;
}
/** General site claim (Professional + Premium + Workflow). Read from the one place the facts live. */
export const TOOL_COUNT_LABEL = SITE_FACTS.toolCountLabel;

export const TRIAL_LINE = `${FREE_DAILY_USES} trial uses a day, no card needed`;
export const DAY_PASS_LINE = `${DAY_PASS_PRICE} Day Pass: unlimited use of file-based tools for 24 hours (sign-in needed to buy)`;
export const MONTHLY_LINE = `${MONTHLY_PRICE} Monthly Pro: unlimited use of file-based tools, ad-free, cancel any time from your Profile`;
